import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { articulosApi } from '../api/notasVenta'
import { articuloPreciosApi } from '../api/listaPrecios'
import BuscadorArticulo from '../components/BuscadorArticulo'
import Boton from '../components/Boton'
import FotosArticuloCard from '../components/FotosArticuloCard'
import useDirtyGuard from '../hooks/useDirtyGuard'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtFechaHora } from '../utils/fecha'

// Visibilidad en la web (mismos códigos que usaba Magento): 1 = solo por link directo · 2 = listado · 3 = búsqueda · 4 = listado + búsqueda.
const VISIBILIDAD = { 1: 'Solo por link directo', 2: 'Listado', 3: 'Búsqueda', 4: 'Listado + búsqueda' }

// ─── Ficha de artículo (/articulos/:id · /articulos/nuevo) ──────────────────
// Maestro COMPLETO (28 campos) + foto (la misma que sirve ligierweb) + precios
// por lista + estado en la web + estado en Presea. El alta/edición puede viajar
// a Presea vía abm_entidades (gateado por PRESEA_ABM_ARTICULOS).

const CAMPOS_VACIOS = {
  codigo_alfa: '', codigo_presea: '', descripcion: '', deta_lista: '',
  rubro: '', subrubro: '', tipo_prod: '', familia: '', cepa: '', marca: '',
  modelo: '', presenta: '', contenido: '', anio: '', bodega: '', pais: '',
  nacional: '', proveedor: '', nomprov: '', codigo_ean: '', cod_alfab1: '',
  iva: '', internos: '', dtoligierc: '', costo: '', bultos: '', activo: true, sin_stock: false,
  web_tipo_regalo: '', web_filtro_regalo: '',
}

// Campos con autocompletado por facetas del maestro (datalist).
const FACETADOS = ['rubro', 'subrubro', 'tipo_prod', 'familia', 'cepa', 'marca', 'bodega', 'presenta', 'contenido', 'pais', 'web_tipo_regalo', 'web_filtro_regalo']

const NUMERICOS = ['iva', 'internos', 'dtoligierc', 'costo', 'bultos', 'anio']

export default function ArticuloFichaPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const esAlta = !id

  const [form, setForm] = useState(CAMPOS_VACIOS)
  const [ficha, setFicha] = useState(null)      // {articulo, precios, foto, web, presea}
  const [facetas, setFacetas] = useState({})
  const [cargando, setCargando] = useState(!esAlta)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const [ok, setOk] = useState('')
  const [tocado, setTocado] = useState(false)
  const [facturaComo, setFacturaComo] = useState(null)   // {id, codigo_alfa, descripcion} | null

  useDirtyGuard(tocado && !guardando)

  // Keep-previous-data: "Cargando…" solo en el primer render (estado inicial);
  // las recargas post-guardar/foto refrescan sin blanquear la pantalla.
  const cargar = useCallback(() => {
    if (esAlta) return
    articulosApi.obtener(id)
      .then(({ data }) => {
        setFicha(data)
        const a = data.articulo
        setForm(Object.fromEntries(Object.keys(CAMPOS_VACIOS).map(k => [k, a[k] ?? (k === 'activo' ? true : (k === 'sin_stock' ? false : ''))])))
        setFacturaComo(a.factura_como || null)
        setTocado(false)
      })
      .catch(() => setError('No se pudo cargar el artículo.'))
      .finally(() => setCargando(false))
  }, [id, esAlta])

  useEffect(() => { cargar() }, [cargar])

  // Facetas para autocompletar (una vez; livianas y solo en esta pantalla).
  useEffect(() => {
    FACETADOS.forEach(campo => {
      articulosApi.facetas(campo)
        .then(({ data }) => setFacetas(f => ({ ...f, [campo]: data })))
        .catch(() => {})
    })
  }, [])

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }))
    setTocado(true)
  }

  const guardar = async () => {
    setGuardando(true); setError(''); setOk('')
    try {
      const payload = { ...form }
      for (const k of NUMERICOS) payload[k] = payload[k] === '' ? null : payload[k]
      for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null
      payload.activo = !!form.activo
      payload.sin_stock = !!form.sin_stock
      payload.factura_como_id = facturaComo?.id ?? null

      if (esAlta) {
        const { data } = await articulosApi.crear(payload)
        setTocado(false)
        navigate(`/articulos/${data.articulo.id}`, { replace: true })
      } else {
        const { data } = await articulosApi.actualizar(id, payload)
        setTocado(false)
        setOk(data.abm_presea?.encolado
          ? 'Guardado — el cambio viaja a Presea y la tienda lo muestra cuando Presea lo confirma (ver «En Presea»).'
          : 'Guardado en LGR.')
        cargar()
      }
    } catch (e) {
      const errs = e.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || 'No se pudo guardar.'))
    } finally { setGuardando(false) }
  }

  // Stock / habilitado de la web desde la ficha (mismo endpoint que el listado).
  const [cambiandoStock, setCambiandoStock] = useState(false)
  const cambiarStockWeb = async (enStock) => {
    setCambiandoStock(true); setError(''); setOk('')
    try {
      const { data } = await articulosApi.stockWeb([Number(id)], enStock)
      setOk(data.mensaje)
      cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cambiar el stock de la web.')
    } finally { setCambiandoStock(false) }
  }
  const cambiarHabilitado = async (habilitado) => {
    setCambiandoStock(true); setError(''); setOk('')
    try {
      await articulosApi.actualizar(id, { web_habilitado: habilitado })
      setOk(habilitado ? 'Habilitado en la web.' : 'Fuera de la web.')
      cargar()
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cambiar.')
    } finally { setCambiandoStock(false) }
  }

  if (cargando) {
    return <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta }}>Cargando…</div>
  }

  const a = ficha?.articulo
  const abmOn = ficha?.presea?.abm_habilitado
  // Artículo que existe en Presea: los descriptivos viajan allá (sug #138) y
  // costo/IVA/internos/proveedor se cambian solo en Presea.
  const enPresea = !esAlta && !!ficha?.presea?.existe
  const modificarOn = enPresea && !!ficha?.presea?.modificar_habilitado
  const soloPresea = (campo) => enPresea && (ficha?.presea?.solo_presea || []).includes(campo)

  return (
    <div style={{ padding: space['2xl'], maxWidth: 1160, margin: '0 auto', fontFamily: font.sans }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: space.md, flexWrap: 'wrap', marginBottom: space.xl }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={st.eyebrow}>STOCK · ARTÍCULO</div>
          <h1 style={{ margin: '2px 0 0', fontSize: text.title + 6, color: colors.text }}>
            {esAlta ? 'Nuevo artículo' : (a?.descripcion || '—')}
          </h1>
          {!esAlta && (
            <div style={{ display: 'flex', gap: space.sm, marginTop: space.sm, flexWrap: 'wrap' }}>
              <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{a?.codigo_alfa}</span>
              {a?.codigo_presea
                ? <span style={{ ...sx.badge, background: colors.zonaBg, color: colors.zonaText }}>Presea {a.codigo_presea}</span>
                : <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>Pendiente en Presea</span>}
              {a?.factura_como && <span style={{ ...sx.badge, background: colors.infoBg, color: colors.info }}>alias → {a.factura_como.codigo_alfa}</span>}
              {!form.activo && <span style={{ ...sx.badge, background: colors.errBg, color: colors.err }}>Inactivo</span>}
              {form.sin_stock && <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>Sin stock — no se vende</span>}
            </div>
          )}
        </div>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
        <Boton variante="primary" onClick={guardar} disabled={guardando || !form.codigo_alfa || !form.descripcion}>
          {guardando ? 'Guardando…' : esAlta ? '💾 Crear artículo' : '💾 Guardar cambios'}
        </Boton>
      </div>

      {error && <div style={st.bannerErr}>{error}</div>}
      {ok && <div style={st.bannerOk}>{ok}</div>}
      {modificarOn && (
        <div style={st.bannerInfo}>
          Lo que cambies en <b>descripción web, bodega, marca, cepa, año, tipo, rubro, subrubro, contenido y EAN</b> se
          guarda también en Presea (en mayúsculas) y se ve en la tienda en unos minutos.
          Costo, IVA, internos y proveedor se cambian en Presea.
        </div>
      )}
      {!abmOn && !modificarOn && (
        <div style={st.bannerInfo}>
          El empuje automático a Presea está <b>apagado</b> (se prende tras validar el ABM contra el entorno de prueba).
          Lo que guardes queda en LGR{esAlta ? ' y el artículo hay que cargarlo también en Presea' : '; si el artículo existe en Presea, el sync nocturno puede pisar estos campos'}.
        </div>
      )}

      <div style={{ display: 'flex', gap: space.xl, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Columna foto */}
        <div style={{ width: 300, flexShrink: 0 }}>
          {esAlta ? (
            <div style={st.card}>
              <h3 style={sx.cardTitle}>Foto</h3>
              <div style={st.fotoBox}><div style={st.fotoPlaceholder}>Guardá el artículo para poder subir la foto</div></div>
            </div>
          ) : (
            <FotosArticuloCard articuloId={id} sku={String(form.codigo_alfa || 'SKU').toUpperCase()} descripcion={form.descripcion}
              foto={ficha?.foto} onCambio={cargar} />
          )}

          {/* Estado web */}
          {!esAlta && (
            <div style={{ ...st.card, marginTop: space.lg }}>
              <h3 style={sx.cardTitle}>En ligierweb</h3>
              <FilaDato label="Publicado" valor={ficha?.web?.publicado ? 'Sí' : 'No (lo marca Presea)'} />
              <FilaDato label="Con foto" valor={ficha?.web?.has_image ? 'Sí' : 'No'} />
              {/* Demanda: quiénes pidieron "avisame cuando vuelva" y siguen esperando */}
              {ficha?.avisos?.pendientes > 0 && (
                <FilaDato label="Esperan este vino" valor={`${ficha.avisos.pendientes} ${ficha.avisos.pendientes === 1 ? 'persona' : 'personas'} · último pedido ${new Date(ficha.avisos.ultimo).toLocaleDateString('es-AR')}`} />
              )}
              {ficha?.web?.publicado && <FilaDato label="Ranking" valor={`tier ${ficha.web.tier} · ${ficha.web.unidades_90d} u/90d`} />}
              {ficha?.web?.publicado && ficha?.web?.url && (
                <div style={{ marginTop: space.md }}>
                  <Boton variante="secondary" sm onClick={() => window.open(ficha.web.url, '_blank')}>Ver en ligierweb ↗</Boton>
                </div>
              )}
            </div>
          )}

          {/* Estado de venta en la WEB — campos propios de LGR (18 sep 2026).
              Hasta el apagado de Magento los sigue pisando su sync (05:50);
              después los alimenta el stock de Presea y la mano del operario. */}
          {!esAlta && (
            <div style={{ ...st.card, marginTop: space.lg }}>
              <h3 style={sx.cardTitle}>En la web (ligier.com.ar)</h3>
              <FilaDato label="Habilitado" valor={a?.web_habilitado ? 'Sí' : (a?.web_habilitado === false ? 'No' : 'Nunca estuvo')} />
              <FilaDato label="En stock" valor={a?.web_en_stock ? 'Sí' : (a?.web_en_stock === false ? 'No' : '—')} />
              <FilaDato label="Visibilidad" valor={VISIBILIDAD[a?.web_visibilidad] || '—'} />
              {a?.web_en_stock_at && (
                <FilaDato label="Stock tocado" valor={new Date(a.web_en_stock_at).toLocaleDateString('es-AR')} />
              )}
              <div style={{ display: 'flex', gap: space.sm, marginTop: space.md, flexWrap: 'wrap' }}>
                <Boton variante={a?.web_en_stock ? 'secondary' : 'success'} sm disabled={cambiandoStock}
                  onClick={() => cambiarStockWeb(!a?.web_en_stock)}>
                  {a?.web_en_stock ? 'Marcar sin stock' : 'Marcar en stock'}
                </Boton>
                {a?.web_habilitado && (
                  <Boton variante="ghost" sm disabled={cambiandoStock} onClick={() => cambiarHabilitado(false)}>
                    Sacar de la web
                  </Boton>
                )}
              </div>
              {a?.magento_sync_at && (
                <div style={{ marginTop: space.md, paddingTop: space.sm, borderTop: `1px solid ${colors.border}` }}>
                  <FilaDato label="Cantidad (Magento)" valor={a.magento_qty != null ? Number(a.magento_qty).toLocaleString('es-AR') : '—'} mono />
                  <FilaDato label="Último sync Magento" valor={new Date(a.magento_sync_at).toLocaleDateString('es-AR')} />
                </div>
              )}
            </div>
          )}

          {/* Estado Presea */}
          {!esAlta && (
            <div style={{ ...st.card, marginTop: space.lg }}>
              <h3 style={sx.cardTitle}>En Presea</h3>
              <FilaDato label="Código" valor={a?.codigo_presea || 'Pendiente'} mono />
              <FilaDato label="Último sync" valor={a?.sync_presea_at ? new Date(a.sync_presea_at).toLocaleDateString('es-AR') : '—'} />
              {ficha?.presea?.cola && (
                <FilaDato label="Último envío" valor={`${ESTADO_ENVIO[ficha.presea.cola.estado] || ficha.presea.cola.estado}${ficha.presea.cola.error ? ' — ' + ficha.presea.cola.error : ''}`} />
              )}
            </div>
          )}
        </div>

        {/* Columna campos */}
        <div style={{ flex: 1, minWidth: 480 }}>
          <Bloque titulo="Identidad">
            <Campo label="Código alfa (SKU) *" valor={form.codigo_alfa} onChange={set('codigo_alfa')} mono disabled={!esAlta} hint={esAlta ? null : 'El SKU no se cambia: es el nombre de la foto y la clave del sync'} />
            <Campo label="Código Presea" valor={form.codigo_presea} onChange={set('codigo_presea')} mono hint="Lo asigna Presea — dejalo vacío en un alta" />
            <Campo label="Descripción *" valor={form.descripcion} onChange={set('descripcion')} ancho />
            <Campo label="Descripción web (deta_lista)" valor={form.deta_lista} onChange={set('deta_lista')} ancho />
            <Campo label="EAN" valor={form.codigo_ean} onChange={set('codigo_ean')} mono />
            <Campo label="SKU del actual (VG)" valor={form.cod_alfab1} onChange={set('cod_alfab1')} mono hint="Solo Vinos Guardados: SKU de la añada actual" />
            <div style={{ ...st.campo, flexBasis: '100%' }}>
              <label style={st.label}>Factura como (alias)</label>
              {facturaComo ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: space.md }}>
                  <span style={{ ...sx.mono, fontSize: text.sec, color: colors.text }}>{facturaComo.codigo_alfa}</span>
                  <span style={{ fontSize: text.sec, color: colors.textSec, flex: 1 }}>{facturaComo.descripcion}</span>
                  <Boton variante="ghost" sm onClick={() => { setFacturaComo(null); setTocado(true) }}>Quitar vínculo</Boton>
                </div>
              ) : (
                <BuscadorArticulo onSelect={a2 => { setFacturaComo(a2); setTocado(true) }} />
              )}
              <span style={st.hint}>Para productos que en Presea se facturan con OTRO código (ej. membresías): a Presea viaja el facturable. Un alias no se da de alta en Presea.</span>
            </div>
          </Bloque>

          <Bloque titulo="Categorización">
            <Campo label="Rubro" valor={form.rubro} onChange={set('rubro')} lista={facetas.rubro} />
            <Campo label="Subrubro" valor={form.subrubro} onChange={set('subrubro')} lista={facetas.subrubro} />
            <Campo label="Tipo de producto" valor={form.tipo_prod} onChange={set('tipo_prod')} lista={facetas.tipo_prod} />
            <Campo label="Familia" valor={form.familia} onChange={set('familia')} lista={facetas.familia} />
            <Campo label="Cepa" valor={form.cepa} onChange={set('cepa')} lista={facetas.cepa} />
            <Campo label="Marca" valor={form.marca} onChange={set('marca')} lista={facetas.marca} />
            <Campo label="Modelo" valor={form.modelo} onChange={set('modelo')} />
            <Campo label="Presentación" valor={form.presenta} onChange={set('presenta')} lista={facetas.presenta} />
            <Campo label="Contenido" valor={form.contenido} onChange={set('contenido')} lista={facetas.contenido} />
            <Campo label="Año" valor={form.anio} onChange={set('anio')} tipo="number" />
          </Bloque>

          <Bloque titulo="Filtros de la tienda (regalos)">
            <Campo label="Tipo de regalo" valor={form.web_tipo_regalo} onChange={set('web_tipo_regalo')} lista={facetas.web_tipo_regalo}
              hint="Lo que ve el cliente en el filtro «Tipo de regalo». Vacío = no aparece en ese filtro." />
            <Campo label="Filtro de regalos" valor={form.web_filtro_regalo} onChange={set('web_filtro_regalo')} lista={facetas.web_filtro_regalo}
              hint="Lo que ve el cliente en «Filtro de Regalos» (ej. Estuches de 2 Botellas). Vacío = no aparece." />
          </Bloque>

          <Bloque titulo="Origen y proveedor">
            <Campo label="Bodega" valor={form.bodega} onChange={set('bodega')} lista={facetas.bodega} />
            <Campo label="País" valor={form.pais} onChange={set('pais')} lista={facetas.pais} />
            <div style={st.campo}>
              <label style={st.label}>Nacional</label>
              <select value={form.nacional || ''} onChange={set('nacional')} style={st.input}>
                <option value="">—</option>
                <option value="S">Sí</option>
                <option value="N">No</option>
              </select>
            </div>
            <Campo label="Cód. proveedor" valor={form.proveedor} onChange={set('proveedor')} mono
              disabled={soloPresea('proveedor')} hint={soloPresea('proveedor') ? 'Se cambia en Presea' : null} />
            <Campo label="Proveedor" valor={form.nomprov} onChange={set('nomprov')} />
          </Bloque>

          <Bloque titulo="Comercial">
            <Campo label="IVA %" valor={form.iva} onChange={set('iva')} tipo="number"
              disabled={soloPresea('iva')} hint={soloPresea('iva') ? 'Se cambia en Presea' : null} />
            <Campo label="Internos %" valor={form.internos} onChange={set('internos')} tipo="number"
              disabled={soloPresea('internos')} hint={soloPresea('internos') ? 'Se cambia en Presea' : null} />
            <Campo label="Dto. Ligier (código)" valor={form.dtoligierc} onChange={set('dtoligierc')} tipo="number"
              hint={ficha?.dto_lgr ? `${ficha.dto_lgr.nombre} — ${Number(ficha.dto_lgr.dto_w)}%` : 'Código de la tabla de descuentos (dto_lgr)'} />
            <Campo label="Costo" valor={form.costo} onChange={set('costo')} tipo="number"
              disabled={soloPresea('costo')} hint={soloPresea('costo') ? 'Se cambia en Presea' : 'Lo actualiza el sync de Presea'} />
            <Campo label="Bultos" valor={form.bultos} onChange={set('bultos')} tipo="number" />
            <div style={st.campo}>
              <label style={st.label}>Activo</label>
              <select value={form.activo ? '1' : '0'} onChange={e => { setForm(f => ({ ...f, activo: e.target.value === '1' })); setTocado(true) }} style={st.input}>
                <option value="1">Sí</option>
                <option value="0">No</option>
              </select>
              <span style={{ fontSize: 11, color: colors.textMeta }}>
                Baja del artículo. Para "se terminó", usá Stock.
              </span>
            </div>
            {/* "Se terminó" NO es dar de baja: el artículo sigue en la tabla y
                vuelve solo cuando entre mercadería. Antes había que usar Activo
                para esto, y eso lo hacía desaparecer del listado sin vuelta. */}
            <div style={st.campo}>
              <label style={st.label}>Stock</label>
              <select
                value={form.sin_stock ? '0' : '1'}
                onChange={e => { setForm(f => ({ ...f, sin_stock: e.target.value === '0' })); setTocado(true) }}
                style={{ ...st.input, ...(form.sin_stock ? { borderColor: colors.warn, background: colors.warnBg } : {}) }}
              >
                <option value="1">Hay stock — se vende</option>
                <option value="0">Sin stock — no se vende</option>
              </select>
              <span style={{ fontSize: 11, color: colors.textMeta }}>
                Sin stock lo saca de la venta en ligier.com.ar y en Magento.
                Vuelve solo cuando entre en una recepción.
              </span>
            </div>
          </Bloque>

          {/* Precios por lista — mismos endpoints que el modal anterior */}
          {!esAlta && <CardPrecios articuloId={Number(id)} precios={ficha?.precios} />}

          {/* Historial de la ficha (sug #117): quién cambió qué y cuándo */}
          {!esAlta && ficha?.cambios?.length > 0 && <CardCambios cambios={ficha.cambios} />}
        </div>
      </div>
    </div>
  )
}

// ─── Sub-componentes ─────────────────────────────────────────────────────────

const ESTADO_ENVIO = {
  pendiente_envio: '⏳ Enviando a Presea',
  enviado: '⏳ Enviando a Presea',
  error: '⏳ Reintentando (sin respuesta de Presea)',
  procesado: '✓ Aplicado en Presea',
  revisar: '✕ A revisar — Presea no confirmó el cambio',
  rechazado: '✕ Presea lo rechazó',
}

const NOMBRE_CAMPO = {
  deta_lista: 'Descripción web', descripcion: 'Descripción', codigo_ean: 'EAN', tipo_prod: 'Tipo de producto',
  anio: 'Año', cepa: 'Cepa', marca: 'Marca', bodega: 'Bodega', rubro: 'Rubro', subrubro: 'Subrubro',
  contenido: 'Contenido', familia: 'Familia', modelo: 'Modelo', presenta: 'Presentación', pais: 'País',
  nomprov: 'Proveedor', cod_alfab1: 'SKU del actual (VG)', bultos: 'Bultos', dtoligierc: 'Dto. Ligier',
  activo: 'Activo', sin_stock: 'Sin stock', codigo_presea: 'Código Presea', factura_como_id: 'Factura como',
  web_habilitado: 'Habilitado en la web', web_en_stock: 'Stock web', web_visibilidad: 'Visibilidad web',
  web_tipo_regalo: 'Tipo de regalo', web_filtro_regalo: 'Filtro de regalos', nacional: 'Nacional',
}

function CardCambios({ cambios }) {
  return (
    <div style={{ ...st.card, marginTop: space.lg }}>
      <h3 style={sx.cardTitle}>Historial de cambios</h3>
      {cambios.map((c, i) => (
        <div key={i} style={{ padding: `${space.sm}px 0`, borderBottom: `1px solid ${colors.borderSoft}`, fontSize: text.sec }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.md, color: colors.textSec }}>
            <span><b style={{ color: colors.text }}>{NOMBRE_CAMPO[c.campo] || c.campo}</b> · {c.usuario || 'Sistema'}</span>
            <span>{fmtFechaHora(c.created_at)}</span>
          </div>
          <div style={{ color: colors.text, marginTop: 2 }}>
            <span style={{ color: colors.textMeta, textDecoration: 'line-through' }}>{c.antes || 'vacío'}</span>
            {' → '}{c.despues || 'vacío'}
            {c.presea_estado && <span style={{ color: colors.textMeta }}> · {ESTADO_ENVIO[c.presea_estado] || c.presea_estado}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

function Bloque({ titulo, children }) {
  return (
    <div style={{ ...st.card, marginBottom: space.lg }}>
      <h3 style={sx.cardTitle}>{titulo}</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.md, marginTop: space.md }}>
        {children}
      </div>
    </div>
  )
}

function Campo({ label, valor, onChange, tipo = 'text', mono = false, ancho = false, lista = null, hint = null, disabled = false }) {
  const listId = lista ? `dl-${label.replace(/\W/g, '')}` : undefined
  return (
    <div style={{ ...st.campo, ...(ancho ? { flexBasis: '100%' } : {}) }}>
      <label style={st.label}>{label}</label>
      <input
        type={tipo}
        value={valor ?? ''}
        onChange={onChange}
        disabled={disabled}
        list={listId}
        style={{ ...st.input, ...(mono ? sx.mono : {}), ...(disabled ? { background: colors.bgSoft, color: colors.textSec } : {}) }}
      />
      {lista && (
        <datalist id={listId}>
          {lista.map(v => <option key={v} value={v} />)}
        </datalist>
      )}
      {hint && <span style={st.hint}>{hint}</span>}
    </div>
  )
}

function FilaDato({ label, valor, mono = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.md, padding: `${space.sm}px 0`, borderBottom: `1px solid ${colors.borderSoft}` }}>
      <span style={{ fontSize: text.sec, color: colors.textSec }}>{label}</span>
      <span style={{ fontSize: text.sec, color: colors.text, textAlign: 'right', ...(mono ? sx.mono : {}) }}>{valor}</span>
    </div>
  )
}

/** Precios por lista — SOLO LECTURA (Martin, 31 ago 2026): por ahora el
 *  precio no se edita desde LGR; lo trae el sync de Presea. */
function CardPrecios({ articuloId, precios }) {
  const [items, setItems] = useState(null)

  useEffect(() => {
    articuloPreciosApi.listar(articuloId)
      .then(({ data }) => setItems(data.data || data))
      .catch(() => setItems([]))
  }, [articuloId])

  const filas = items ?? precios ?? []

  return (
    <div style={st.card}>
      <h3 style={sx.cardTitle}>Precios por lista</h3>
      {items === null ? (
        <p style={{ color: colors.textMeta, fontSize: text.sec }}>Cargando…</p>
      ) : filas.length === 0 ? (
        <p style={{ color: colors.textMeta, fontSize: text.sec }}>Sin precios cargados (los trae el sync de Presea).</p>
      ) : (
        <div style={{ marginTop: space.sm }}>
          {filas.map(p => (
            <div key={p.lista_id ?? p.lista} style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: `${space.sm}px 0`, borderBottom: `1px solid ${colors.borderSoft}` }}>
              <span style={{ flex: 1, fontSize: text.sec, color: colors.textSec }}>{p.lista_nombre ?? p.lista}</span>
              {p.pr_lista != null && (
                <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta, whiteSpace: 'nowrap' }}>
                  lista ${Number(p.pr_lista).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  {Number(p.dto_1) > 0 && ` −${Number(p.dto_1)}%`}
                  {Number(p.dto_2) > 0 && ` −${Number(p.dto_2)}%`}
                </span>
              )}
              <span style={{ ...sx.mono, fontSize: text.body, fontWeight: 600, color: colors.text }}>
                ${Number(p.precio).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          ))}
          <p style={st.nota}>El precio maestro se edita en Presea — el sync lo trae a LGR.</p>
        </div>
      )}
    </div>
  )
}

const st = {
  eyebrow: { fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.06em' },
  card: { ...sx.card },
  fotoBox: { marginTop: space.md, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.bgSoft, borderRadius: radius.sm, overflow: 'hidden' },
  fotoPlaceholder: { color: colors.textPlh, fontSize: text.sec, padding: space.xl, textAlign: 'center' },
  nota: { fontSize: text.meta, color: colors.textMeta, marginTop: space.sm, marginBottom: 0 },
  campo: { display: 'flex', flexDirection: 'column', gap: space.xs, flexBasis: `calc(50% - ${space.md}px)`, minWidth: 180 },
  label: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: `${space.md}px 10px`, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: font.sans, color: colors.text, background: colors.bg },
  hint: { fontSize: text.meta, color: colors.textMeta },
  bannerErr: { background: colors.errBg, color: colors.err, border: `1px solid ${colors.err}33`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, fontSize: text.sec },
  bannerOk: { background: colors.okBg, color: colors.ok, border: `1px solid ${colors.ok}33`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, fontSize: text.sec },
  bannerInfo: { background: colors.infoBg, color: colors.info, border: `1px solid ${colors.info}33`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, fontSize: text.sec },
}
